import { ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { StoryboardPage } from './storyboardPages';

interface Props {
  pages: StoryboardPage[];
  onOpen: (version: number) => void;
  /** Changes made on the page and not sent yet. */
  pending?: number;
}

/** Every storyboard page you can open (Mood board, Plan, Review and their earlier versions), in the view bar. */
export function StoryboardPageMenu({ pages, onOpen, pending = 0 }: Props) {
  const here = pages.find((p) => p.here) ?? pages[0];
  if (!here) return null;
  const unsent = pending > 0 ? `${pending} unsent change${pending === 1 ? '' : 's'}` : null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-[13px] font-medium" aria-label={`Storyboard page: ${here.label}${unsent ? `, ${unsent}` : ''}`}>
          <span className="truncate">{here.label}</span>
          {unsent && (
            <span data-testid="unsent-count" title={unsent} className="min-w-[18px] rounded-full bg-amber-500/15 px-1.5 text-center text-[11px] leading-[18px] tabular-nums text-amber-700 dark:text-amber-400">
              {pending}
            </span>
          )}
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Storyboard pages</DropdownMenuLabel>
        {pages.map((p) => (
          <DropdownMenuItem key={p.version} onSelect={() => !p.here && onOpen(p.version)} className="flex items-start gap-2 py-2">
            <Check aria-hidden="true" className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', p.here ? 'text-primary' : 'invisible')} />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-medium">{p.label}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">{p.newest ? 'Newest' : p.date}</span>
              </span>
              {p.note && <span className="block truncate text-xs text-muted-foreground">{p.note}</span>}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
